import { useDispatch } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { trackInternalEvent } from '@rio/tracking'
import throttle from 'lodash/throttle'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions } from 'builder/modules/resumeEditor'
import { FetchStatuses } from 'builder/modules/constants'
import { EditorPanelType } from 'builder/modules/generalEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { EditorPanelSwitcher } from 'builder/components/EditorPanelSwitcher'
import { ButtonTheme, ButtonV2 } from 'builder/components/Button'
import { SpinnerWhite } from 'builder/components/Spinner'
import { Icon24 } from 'builder/components/Icon'
import { useRemainingAiResumeReviewFreeAttempts } from 'builder/hooks/useRemainingAiResumeReviewFreeAttempts'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { AiResumeReviewWelcomeStep } from '../AiResumeReviewWelcomeStep/AiResumeReviewWelcomeStep'
import { AiResumeReviewLoader } from '../AiResumeReviewLoader/AiResumeReviewLoader'
import { AiResumeReviewError } from '../AiResumeReviewError/AiResumeReviewError'
import { AiResumeReviewResult } from '../AiResumeReviewResult/AiResumeReviewResult'
import { TRANSLATION_KEY } from '../../constants'
import { Container, EditorPanelSwitcherWrapper, FooterContainer } from './styles'

type Props = {
  editorPanelType?: EditorPanelType
  handleEditorPanelTypeChange: (type: EditorPanelType) => void
}

export const AiResumeReviewMobilePanel = (props: Props) => {
  const { editorPanelType, handleEditorPanelTypeChange } = props

  const reviewResult = useTypedSelector(state => state.resumeEditor.aiResumeReview.reviewResult)
  const fetchStatus = useTypedSelector(state => state.resumeEditor.aiResumeReview.fetchStatus)
  const dispatch = useDispatch()
  const { i18n } = useI18n()
  const contentRef = useRef<HTMLDivElement>(null)
  const remainingAttempts = useRemainingAiResumeReviewFreeAttempts()
  const { isPhone } = useMediaQueries()
  const buttonSize = isPhone ? 'small' : 'default'
  const [isHeaderShadowVisible, setIsHeaderShadowVisible] = useState(false)
  const [isFooterShadowVisible, setIsFooterShadowVisible] = useState(false)

  useEffect(() => {
    const element = contentRef.current
    if (!element) return
    disableBodyScroll(element)
    return () => {
      enableBodyScroll(element)
    }
  }, [])

  useEffect(() => {
    const element = contentRef.current
    if (!element) return
    const handleScroll = throttle(
      () => {
        setIsHeaderShadowVisible(element.scrollTop > 0)
        setIsFooterShadowVisible(element.scrollTop + element.clientHeight < element.scrollHeight)
      },
      200,
      { leading: true, trailing: true },
    )
    handleScroll()
    element.addEventListener('scroll', handleScroll)
    return () => {
      element.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleStartReview = () => {
    trackInternalEvent('click_get_better', {
      label: 'ai_review_tab',
    })
    if (remainingAttempts > 0) {
      dispatch(actions.startAiResumeReview())
    } else {
      dispatch(actions.setIsAiResumeReviewPaywallModalOpen(true))
    }
  }

  const renderContent = () => {
    if (fetchStatus === FetchStatuses.loading) {
      return (
        <>
          <AiResumeReviewLoader />
          <FooterContainer $isFooterShadowVisible={isFooterShadowVisible}>
            <ButtonV2 theme={ButtonTheme.alternative} size={buttonSize}>
              <SpinnerWhite $width="24px" $height="24px" />
            </ButtonV2>
          </FooterContainer>
        </>
      )
    }

    if (fetchStatus === FetchStatuses.failed) {
      return (
        <>
          <AiResumeReviewError />
          <FooterContainer $isFooterShadowVisible={isFooterShadowVisible}>
            <ButtonV2 theme={ButtonTheme.ghost} onClick={handleStartReview} size={buttonSize}>
              {i18n.t(`${TRANSLATION_KEY}.error.retry`)}
            </ButtonV2>
          </FooterContainer>
        </>
      )
    }

    if (!reviewResult) {
      return (
        <>
          <AiResumeReviewWelcomeStep />
          <FooterContainer $isFooterShadowVisible={isFooterShadowVisible}>
            <ButtonV2
              theme={ButtonTheme.alternative}
              leadingIcon={<Icon24.FeaturedRegular />}
              onClick={handleStartReview}
              size={buttonSize}
            >
              {i18n.t(`${TRANSLATION_KEY}.mobile_footer.get_review`)}
            </ButtonV2>
          </FooterContainer>
        </>
      )
    }

    return (
      <>
        <AiResumeReviewResult data={reviewResult} />
        <FooterContainer $isFooterShadowVisible={isFooterShadowVisible}>
          <ButtonV2
            theme={ButtonTheme.secondary}
            onClick={() => {
              handleStartReview()
              trackInternalEvent('click_refresh', { label: 'ai_review' })
            }}
            leadingIcon={<Icon24.TurnLeftRegular />}
            size={buttonSize}
          >
            {i18n.t(`${TRANSLATION_KEY}.result.refresh`)}
          </ButtonV2>
        </FooterContainer>
      </>
    )
  }

  return (
    <Container ref={contentRef}>
      <EditorPanelSwitcherWrapper $isHeaderShadowVisible={isHeaderShadowVisible}>
        <EditorPanelSwitcher
          activePanelType={editorPanelType}
          handlePanelTypeChange={handleEditorPanelTypeChange}
          items={[
            { text: i18n.t('builder.resume_editor.edit'), id: 'editor' },
            { text: i18n.t('builder.resume_editor.preview'), id: 'customization' },
            {
              text: i18n.t('builder.resume_editor.ai_review'),
              id: 'ai_review',
            },
          ]}
        />
      </EditorPanelSwitcherWrapper>
      {renderContent()}
    </Container>
  )
}
